import { useEffect, useState } from "react";

import { distanceMeters, formatDistance } from "../services/tacticalMap";

const RETICLE_LINE = (direction: "to right" | "to bottom") =>
  `repeating-linear-gradient(${direction}, rgb(var(--c-primary) / 0.55) 0 4px, transparent 4px 9px)`;

interface Pointer {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

export interface TacticalCrosshairProps {
  map: google.maps.Map | null;
  targetLat: number | null;
  targetLng: number | null;
  targetLabel: string | null;
}

export default function TacticalCrosshair({
  map,
  targetLat,
  targetLng,
  targetLabel,
}: TacticalCrosshairProps) {
  const [pointer, setPointer] = useState<Pointer | null>(null);
  const [dragging, setDragging] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!map) return;

    const div = map.getDiv();

    const listeners = [
      map.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
        const domEvent = event.domEvent as MouseEvent | undefined;
        if (!event.latLng || !domEvent) return;
        const rect = div.getBoundingClientRect();
        setSize({ width: rect.width, height: rect.height });
        setPointer({
          x: domEvent.clientX - rect.left,
          y: domEvent.clientY - rect.top,
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      }),
      map.addListener("mouseout", () => setPointer(null)),
      map.addListener("dragstart", () => setDragging(true)),
      map.addListener("dragend", () => setDragging(false)),
    ];

    return () => listeners.forEach((listener) => listener.remove());
  }, [map]);

  if (!pointer || dragging) return null;

  const flipHorizontal = pointer.x > size.width - 150;
  const flipVertical = pointer.y > size.height - 60;

  const distance =
    targetLat != null && targetLng != null
      ? distanceMeters(
          { lat: pointer.lat, lng: pointer.lng },
          { lat: targetLat, lng: targetLng },
        )
      : null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[3] overflow-hidden">
      <span
        className="absolute inset-x-0 h-px"
        style={{ top: pointer.y, backgroundImage: RETICLE_LINE("to right") }}
      />
      <span
        className="absolute inset-y-0 w-px"
        style={{ left: pointer.x, backgroundImage: RETICLE_LINE("to bottom") }}
      />

      <span
        className="absolute top-0 h-2 w-px"
        style={{ left: pointer.x, background: "rgb(var(--c-primary))" }}
      />
      <span
        className="absolute bottom-0 h-2 w-px"
        style={{ left: pointer.x, background: "rgb(var(--c-primary))" }}
      />
      <span
        className="absolute left-0 h-px w-2"
        style={{ top: pointer.y, background: "rgb(var(--c-primary))" }}
      />
      <span
        className="absolute right-0 h-px w-2"
        style={{ top: pointer.y, background: "rgb(var(--c-primary))" }}
      />

      <div
        className="absolute whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[9.5px] leading-relaxed tracking-wide text-content"
        style={{
          left: flipHorizontal ? undefined : pointer.x + 14,
          right: flipHorizontal ? size.width - pointer.x + 14 : undefined,
          top: flipVertical ? undefined : pointer.y + 14,
          bottom: flipVertical ? size.height - pointer.y + 14 : undefined,
          background: "rgb(var(--c-surface) / 0.9)",
          border: "1px solid rgb(var(--c-primary) / 0.4)",
        }}
      >
        <div>
          {pointer.lat.toFixed(4)}°, {pointer.lng.toFixed(4)}°
        </div>
        {distance !== null && targetLabel ? (
          <div
            className="mt-0.5 border-t pt-0.5 text-content-secondary"
            style={{ borderColor: "rgb(var(--c-primary) / 0.25)" }}
          >
            {targetLabel} · {formatDistance(distance)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
